import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import Country from '../../../../types/country/Country';
import CountryWriteDTO from '../../../../types/country/CountryWriteDTO';
import Page from '../../../../types/shared/Page';
import { IsAdminDirective } from '../../directives/is-admin.directive';
import { CountryService } from '../../services/country.service';
import { ExportService } from '../../services/export.service';
import {
    CreateCountryDialogComponent
} from '../shared/countries/create-country-dialog/create-country-dialog.component';
import {
    UpdateCountryDialogComponent
} from '../shared/countries/update-country-dialog/update-country-dialog.component';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';

@Component({
    selector: 'app-countries',
    templateUrl: './countries.component.html',
    styleUrls: [ './countries.component.scss' ],
    imports: [
        PageHeaderComponent,
        IconField,
        InputIcon,
        FormsModule,
        TableModule,
        Button,
        SharedModule,
        CreateCountryDialogComponent,
        UpdateCountryDialogComponent,
        InputText,
        RouterLink,
        IftaLabelModule,
        IsAdminDirective,
        Tooltip
    ]
})
export class CountriesComponent implements OnInit {
    public search = '';
    public countries: Country[] = [];
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateCountryDialog = false;
    public displayUpdateCountryDialog = false;
    public selectedCountry: CountryWriteDTO = new CountryWriteDTO({});

    private searchSubject = new Subject<string>();

    constructor(private countryService: CountryService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService,
                private exportService: ExportService) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadCountries();

        // Debounce the search input to prevent too many requests
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0; // Reset to the first page on new search
            this.loadCountries();
        });
    }

    /**
     * Handles the page change event.
     *
     * @param event The event.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadCountries();
    }

    /**
     * Handles the search input change event.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Edits a country.
     *
     * @param country The country to edit.
     */
    public editCountry(country: Country): void {
        this.selectedCountry = new CountryWriteDTO(country);
        this.displayUpdateCountryDialog = true;
    }

    /**
     * Deletes a country.
     *
     * @param country The country to delete.
     */
    public deleteCountry(country: Country): void {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete the country ' + country.name + '?',
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.countryService.deleteCountry(country.id).then(() => {
                    this.loadCountries();
                    this.messageService.add(
                        { severity: 'success', summary: 'Success', detail: 'Country deleted', life: 3000 });
                }).catch((error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    /**
     * Opens the create country dialog.
     */
    public openCreateCountryDialog(): void {
        this.displayCreateCountryDialog = true;
    }

    /**
     * Reloads the countries.
     */
    public reloadCountries(): void {
        this.loadCountries();
    }

    /**
     * Exports the countries.
     */
    public exportCountries() {
        this.exportService.exportCountries()
            .then(() => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Countries exported successfully',
                    life: 3000,
                });
            }).catch((error) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
        });
    }

    /**
     * Loads the countries.
     */
    private loadCountries(): void {
        this.loading = true;

        const size = this.rows;
        const search = this.search || '';

        this.countryService
            .getCountries(search, this.currentPage, size)
            .then((result: Page<Country>) => {
                this.countries = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            })
            .finally(() => {
                this.loading = false;
            });
    }
}
